import type { AmReason } from "../../../lib/crm-types";
import { cn } from "../../ui/cn";

/**
 * Why an account is flagged.
 *
 * Modelled on the sales desk's `AttentionBadge`, and for the same reason the
 * brief states outright: **never colour alone**. A red pill and a grey pill are
 * the same pill to roughly one man in twelve, and this is a screen whose entire
 * purpose is that the troubled accounts stand out. So every chip carries a
 * glyph and a word as well as a tint, and the tooltip gives the measurement.
 *
 * The reason text comes from the server, which is also where the flag is
 * decided -- see `am-risk.ts`, where `atRisk` is derived from the reason list
 * rather than set beside it. A flagged account with nothing to show here is
 * therefore not something this component has to handle: it cannot occur.
 */
export function RiskBadge({
  reason,
  size = "sm",
}: {
  reason: AmReason;
  size?: "sm" | "xs";
}) {
  return (
    <span
      title={reason.detail}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-full font-medium",
        /* Tinted rather than solid. There can be sixty of these on screen at
           once, and sixty solid reds is a wall nobody reads. */
        toneOf(reason.code),
        size === "xs" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs",
      )}
    >
      <Glyph code={reason.code} />
      {reason.label}
    </span>
  );
}

/** All the reasons on one row, most severe first. */
export function RiskBadges({
  reasons,
  size = "xs",
  max = 3,
}: {
  reasons: AmReason[];
  size?: "sm" | "xs";
  /** Beyond this, a count. Six chips on a table row is a paragraph. */
  max?: number;
}) {
  if (reasons.length === 0) {
    return <span className="text-[11px] text-faint">—</span>;
  }

  const sorted = [...reasons].sort((a, b) => b.severity - a.severity);
  const shown = sorted.slice(0, max);
  const rest = sorted.slice(max);

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((reason) => (
        <RiskBadge key={reason.code} reason={reason} size={size} />
      ))}
      {rest.length > 0 ? (
        <span
          title={rest.map((reason) => reason.detail).join("\n")}
          className="text-[10px] text-faint"
        >
          +{rest.length}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Colour by what kind of problem it is, not by severity.
 *
 * An open quote is an opportunity rather than neglect, so it is not dressed as
 * a warning -- a manager scanning for red should find accounts going wrong, not
 * accounts with a live quote on them.
 */
function toneOf(code: AmReason["code"]): string {
  if (code === "quote_waiting") {
    return "bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300";
  }
  if (code === "spend_down" || code === "never_ordered") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300";
  }
  return "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300";
}

/**
 * Drawn, not typed.
 *
 * A `⚠` renders as a colour emoji on macOS and a hollow outline on Windows, at
 * different sizes -- and a chip whose height changes per platform quietly
 * breaks a table's row rhythm.
 */
function Glyph({ code }: { code: AmReason["code"] }) {
  const common = {
    width: 10,
    height: 10,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    "aria-hidden": true,
  };

  if (code === "quote_waiting") {
    /* A document, for a quote sitting unanswered. */
    return (
      <svg {...common} strokeWidth="2.5" strokeLinejoin="round">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
      </svg>
    );
  }

  if (code === "spend_down") {
    /* An arrow going down, so the direction is legible without the word. */
    return (
      <svg {...common} strokeWidth="2.5" strokeLinejoin="round">
        <path d="M5 8l7 8 7-8" />
      </svg>
    );
  }

  if (code === "no_contact" || code === "never_ordered") {
    /* A clock: this is about elapsed time, not about a mistake. */
    return (
      <svg {...common} strokeWidth="2.5">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  }

  return (
    <svg {...common} strokeWidth="3">
      <path d="M12 5v9" />
      <path d="M12 19h.01" />
    </svg>
  );
}
